import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

export const namespaced = true;

export const state = {
  darkMode: false,
};

export const mutations = {
  SET_DARK_MODE(state, isDark) {
    state.darkMode = isDark;
  },
};

const fs = firebase.firestore;

export const actions = {
  /**
   * Toggle between light and dark themes
   * Switches the current theme state and persists to Firestore
   */
  async toggleTheme({ commit, state, rootState }) {
    const newThemeState = !state.darkMode;
    commit("SET_DARK_MODE", newThemeState);
    
    // Save to Firestore if user is authenticated
    if (rootState.user.user) {
      try {
        await saveThemePreference(newThemeState, rootState.user.user.uid);
      } catch (error) {
        // console.error("Failed to save theme preference:", error);
        // Show user-friendly notification
        // Theme still works in UI even if save fails
      }
    }
  },

  /**
   * Load theme preference from Firestore
   * Automatically uses emulator when VUE_APP_USE_LOCAL_EMULATORS=true
   */
  async loadThemePreference({ commit, rootState }) {
    // Only load if user is authenticated
    if (!rootState.user.user) {
      commit("SET_DARK_MODE", false);
      return;
    }

    try {
      const userId = rootState.user.user.uid;
      const userDoc = await fs()
        .collection("users")
        .doc(userId)
        .get();

      if (userDoc.exists) {
        const userData = userDoc.data();
        const darkMode = userData.preferences?.darkMode ?? false;
        commit("SET_DARK_MODE", darkMode);
      } else {
        // No user document, default to light mode
        commit("SET_DARK_MODE", false);
      }
    } catch (error) {
      // console.error("Failed to load theme preference:", error);
      // Default to light mode on error
      commit("SET_DARK_MODE", false);
    }
  },

  /**
   * Save theme preference to Firestore
   * Automatically uses emulator when VUE_APP_USE_LOCAL_EMULATORS=true
   */
  async saveThemePreference({ state, rootState }) {
    // Only save if user is authenticated
    if (!rootState.user.user) {
      return;
    }

    try {
      const userId = rootState.user.user.uid;
      await saveThemePreference(state.darkMode, userId);
    } catch (error) {
      // console.error("Failed to save theme preference:", error);
      // Retry once after 1 second
      setTimeout(async () => {
        try {
          const userId = rootState.user.user.uid;
          await saveThemePreference(state.darkMode, userId);
        } catch (retryError) {
          // console.error("Retry failed to save theme preference:", retryError);
          // Could show toast notification here
        }
      }, 1000);
    }
  },

  /**
   * Initialize theme on app load
   * Loads saved preference or defaults to light mode
   */
  async initializeTheme({ dispatch, rootState }) {
    // Only initialize if user is authenticated
    if (rootState.user.user) {
      await dispatch("loadThemePreference");
    }
  },
};

export const getters = {
  isDarkMode(state) {
    return state.darkMode;
  },
};

// Private helper function
async function saveThemePreference(darkMode, userId) {
  await fs()
    .collection("users")
    .doc(userId)
    .set(
      {
        preferences: {
          darkMode: darkMode,
        },
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      },
      { merge: true }
    );
}
